import React, { useEffect, useMemo, useRef, useState } from "react";
import { AdSpot, AdState } from "../../../../../core/orderAd/orderAd.entity";
import { strings } from "../../../../../intl/fa";
import styles from "../OrderDetails.module.scss";
import { useAppDispatch, useAppSelector } from "../../../../../utils/store";
import { AdMainContent, AdRecordProps, PaymentFooter } from "../DetailCard";
import {
  AppPage,
  NavigationActions,
} from "../../../../../core/navigation/navigation.slice";
import { useRequest } from "../../../../../hooks/useRequest";
import { OrderReport } from "../orderReport/OrderReport";
import { NpsModal } from "../nps/modal/NpsModal";
import {
  ApiBonusCodeRecord,
  BonusCodeType,
} from "../../../../../core/code/code.entity";
import LineChart from "../lineChart/LineChart";
import { AdPriceActions } from "core/adPrice/adPrice.slice";
import { ArrowIcon, EditIcon } from "@bale/ui";
import { useTheme } from "@emotion/react";
import {
  getJalaliDateObject,
  JalaliToRawPersian,
  numberToHour,
} from "utils/time";
import { ProgressSteps } from "components/commons/progressSteps/ProgressSteps";
import { isBaleLink } from "utils/payment";
import { JoinOrLeftChart } from "../../leftOrJoinChart/JoinOrLeftChart";
import { digitsToHindi } from "@bale/utils";
import { GetMemberCountThunk } from "core/membersCount/membersCount.thunk";
import { getAdPrice, GetAdPriceResponse } from "core/adPrice/adPrice.api";
import { toast } from "react-toastify";

const steps = {
  [AdState.AwaitingContentApproval]: {
    label: strings.ad_states_labels.AwaitingContentApproval.replace(/\./g, ""),
    step: 1,
  },
  [AdState.AwaitingPayment]: {
    label: strings.ad_states_labels.AwaitingPayment,
    step: 2,
  },
  [AdState.AwaitingToShow]: {
    label: strings.ad_states_labels.AwaitingToShow,
    step: 3,
  },
  [AdState.Active]: {
    label: strings.ad_states_labels.Active,
    step: 4,
  },
  [AdState.Finished]: {
    label: strings.ad_states_labels.Finished,
    step: 5,
  },
};

const AwaitingApproval: React.FC<React.PropsWithChildren<AdRecordProps>> = (
  props
) => {
  return (
    <>
      <AdMainContent adRecord={props.adRecord} />
    </>
  );
};

const AwaitingPayment: React.FC<React.PropsWithChildren<AdRecordProps>> = (
  props
) => {
  const { sendRequest: getMemberCount } = useRequest(GetMemberCountThunk);
  const [bonusView, setBonusView] = useState<number | undefined>(undefined);
  const [bonusClick, setBonusClick] = useState<number | undefined>(undefined);
  const requestedBonusCodeRef = useRef<ApiBonusCodeRecord>();

  const dispatch = useAppDispatch();

  const isSponsoredMessage =
    props.adRecord.spot === AdSpot.AdSpotSponsoredMessage;
  const adPrice = useAppSelector((state) => state.adPrice.adPrice);
  const selectedOrder = useAppSelector(
    (state) => state.ordersList.selectedOrder
  );
  const selectedTime = useAppSelector(
    (state) => state.periodCapacityData.selectedTime
  );

  const [price, setPrice] = useState<GetAdPriceResponse>();

  useEffect(() => {
    getAdPrice({ ad_id: props.adRecord.id }).then((res) => {
      setPrice(res.response);
    });
  }, [props.adRecord]);

  useEffect(() => {
    if (isBaleLink(selectedOrder?.link)) {
      getMemberCount({ link: selectedOrder?.link! });
    }

    if (price?.total_price && price.final_price)
      dispatch(
        AdPriceActions.setAdPrice({
          discounted_amount: price.discounted_amount,
          final_price: price?.final_price,
          total_price: price?.total_price,
        })
      );
  }, [selectedOrder?.link, isSponsoredMessage, price]);

  function codeApplier(code: ApiBonusCodeRecord) {
    if (
      requestedBonusCodeRef.current &&
      requestedBonusCodeRef.current.code === code.code
    ) {
      toast.info(strings.bonus_code_errors.alreadyInquired);
      return;
    }
    requestedBonusCodeRef.current = code;
    const max = code.discount_celling;
    let newValue;
    switch (code.type) {
      case BonusCodeType.ClickFixed:
        setBonusClick(code.value);
        break;
      case BonusCodeType.ClickPercent:
        newValue = (code.value / 100) * props.adRecord.click_count;
        setBonusClick(Math.floor(Math.min(newValue, max)));
        break;
      case BonusCodeType.ViewFixed:
        setBonusView(code.value);
        break;
      case BonusCodeType.ViewPercent:
        newValue = (code.value / 100) * props.adRecord.view_count;
        setBonusView(Math.floor(Math.min(newValue, max)));
        break;
      case BonusCodeType.ClickAndViewPercent:
        newValue = (code.value / 100) * props.adRecord.click_count;
        setBonusClick(Math.floor(Math.min(newValue, max)));
        let newView = (code.value / 100) * props.adRecord.view_count;
        setBonusView(Math.floor(Math.min(newView, max)));
        break;
      case BonusCodeType.PriceDiscount:
        const discount = (adPrice.final_price * code.value) / 100;
        const controlled_discount = Math.min(discount, max);
        dispatch(
          AdPriceActions.setAdPrice({
            total_price: adPrice.total_price,
            discounted_amount: controlled_discount,
            final_price: adPrice.final_price - controlled_discount,
          })
        );
        break;
    }
    toast.info(strings.bonus_code_input.good_bonus_code);
  }

  return (
    <>
      <AdMainContent adRecord={{ ...props.adRecord, bonusView, bonusClick }}>
        <AdTimeCard />
      </AdMainContent>
      <PaymentFooter
        adRecord={props.adRecord}
        bonusCode={requestedBonusCodeRef.current?.code}
        adPrice={adPrice}
        selectedTime={selectedTime}
        disableButton={!props.adRecord.is_instant && !selectedTime.date}
        setBonusCode={codeApplier}
      />
    </>
  );
};

const Finished: React.FC<React.PropsWithChildren<AdRecordProps>> = (props) => {
  const orderReport = useAppSelector((state) => state.ordersList.orderReport);
  return (
    <div className={styles.ScrollableDetails}>
      <AdMainContent adRecord={props.adRecord}>
        {orderReport && (
          <OrderReport reportData={orderReport} spot={props.adRecord.spot} />
        )}
        <LineChart adRecord={props.adRecord} />
        <JoinOrLeftChart adRecord={props.adRecord} />
      </AdMainContent>
      <NpsModal adId={props.adRecord.id} />
    </div>
  );
};

const Active: React.FC<React.PropsWithChildren<AdRecordProps>> = (props) => {
  const orderReport = useAppSelector((state) => state.ordersList.orderReport);
  return (
    <>
      <AdMainContent adRecord={props.adRecord}>
        {orderReport && (
          <OrderReport
            reportData={orderReport}
            isActive
            spot={props.adRecord.spot}
          />
        )}
      </AdMainContent>
    </>
  );
};

export function AdTimeCard() {
  const [isTooltipOpen, setIsTooltipOpen] = useState(true);
  const selectedTime = useAppSelector(
    (state) => state.periodCapacityData.selectedTime
  );
  const dispatch = useAppDispatch();
  const isInstant = useAppSelector(
    (state) => state.ordersList.selectedOrder?.is_instant
  );
  const theme = useTheme();

  function openAdTimeSelect() {
    dispatch(NavigationActions.setAppPage(AppPage.AdTimeSelect));
  }

  useEffect(() => {
    let timerId = setTimeout(() => {
      setIsTooltipOpen(false);
    }, 10 * 1000);
    return () => {
      clearTimeout(timerId);
    };
  }, []);

  return (
    <div className={styles.Card}>
      <div className={styles.InfoWrapper}>
        <div className={styles.Row}>
          <p className={styles.Key}>{strings.order_details.show_date}</p>
          {isInstant ? (
            <p className={styles.SelectedTime}>دقایقی پس از پرداخت</p>
          ) : selectedTime.date ? (
            <div className={`${styles.SelectedTime} ${styles.Tooltip}`}>
              {isTooltipOpen && (
                <div
                  className={styles.TooltipText}
                  onClick={() => setIsTooltipOpen(false)}
                >
                  {strings.ad_time_select.reserve_on_select_date}
                </div>
              )}
              <EditIcon
                size={20}
                color={theme.colors.primary[50]}
                onClick={openAdTimeSelect}
                cursor="pointer"
              />
              <div>
                {digitsToHindi(numberToHour(selectedTime.hour))}
                {" - "}
                {digitsToHindi(
                  JalaliToRawPersian(getJalaliDateObject(selectedTime.date))
                )}
              </div>
            </div>
          ) : (
            <div className={`${styles.SelectTime} ${styles.Tooltip}`}>
              {isTooltipOpen && (
                <div
                  className={styles.TooltipText}
                  onClick={() => setIsTooltipOpen(false)}
                >
                  {strings.order_details.select_date_tooltip}
                </div>
              )}
              <div onClick={openAdTimeSelect}>
                {strings.order_details.choose_time}
              </div>
              <ArrowIcon size={20} color={theme.colors.primary[50]} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const DialogAdDetails: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const selectedAd = useAppSelector((state) => state.ordersList.selectedOrder);
  const Content = useMemo(() => {
    switch (selectedAd?.state) {
      case AdState.AwaitingContentApproval:
        return AwaitingApproval;
      case AdState.AwaitingPayment:
        return AwaitingPayment;
      case AdState.Active:
        return Active;
      case AdState.Finished:
        return Finished;
      default:
        return AdMainContent;
    }
  }, [selectedAd]);

  if (!selectedAd) return null;

  return (
    <>
      {steps[selectedAd?.state as keyof typeof steps] && (
        <ProgressSteps
          steps={Object.values(steps)}
          currentStep={steps[selectedAd?.state as keyof typeof steps]?.step}
          showOnmobile={false}
        />
      )}
      {/* <div className={styles.section}>
        <p className={styles.sectionTitle}>{"اطلاعات تبلیغ"}</p> */}
      <Content adRecord={selectedAd} />
      {/* </div> */}
    </>
  );
};
